/* eslint-disable no-unused-expressions */
import React, { createElement, PropsWithChildren } from 'react';
import {
  sendNativeMessage as coreSendNativeMessage,
  NativeMessageEvent,
  isCarvanaNativeApp,
} from '@carvana/core-native-messaging';

export type { NativeMessageEvent };

const ensureAbsoluteUrl = (url: string): string => {
  if (!url) return url;

  // Don't modify URLs that:
  // 1. Start with a protocol (http:, https:, tel:, mailto:, etc)
  // 2. Start with // (protocol-relative URLs)
  // 3. Start with # (hash routes)
  // 4. Start with ? (query strings)
  if (
    /^[a-zA-Z][a-zA-Z0-9+.-]*:/i.test(url) || // Has protocol
    url.startsWith('//') ||
    url.startsWith('#') ||
    url.startsWith('?')
  ) {
    return url;
  }

  // At this point, we're dealing with a relative path that needs to be made absolute
  return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
};

export const sendNativeMessage = (event: NativeMessageEvent, payload?: Record<string, any>, source?: string) => {
  return coreSendNativeMessage(event, payload || {}, source || 'my_cars');
};

const sendNativeMessageWithAbsoluteUrl = (event: NativeMessageEvent, payload?: Record<string, any>) => {
  const modifiedPayload = payload ? { ...payload } : {};

  // Convert url in payload if it exists
  if (modifiedPayload.url) {
    modifiedPayload.url = ensureAbsoluteUrl(modifiedPayload.url);
  }

  sendNativeMessage(event, modifiedPayload);
};

type NativeEventPayload = {
  event: string;
  payload?: Record<string, any>;
};

export type LinkProps = {
  onClick?: () => void;
  nativeEvent?: NativeEventPayload;
  [key: string]: any;
  as?: HTMLElement;
} & PropsWithChildren &
  React.HTMLProps<HTMLAnchorElement>;

export const LinkWithNativeEvents = ({ children, as, onClick, nativeEvent, ...props }: LinkProps) => {
  const HtmlType = as || 'a';
  const handleClick = (e) => {
    if (isCarvanaNativeApp()) {
      if (e) e.preventDefault();

      sendNativeMessageWithAbsoluteUrl((nativeEvent?.event || 'link') as NativeMessageEvent, {
        ...nativeEvent?.payload,
        ...(!nativeEvent?.payload?.url &&
          props.href && {
            url: props.href,
          }),
      });
    } else {
      onClick?.(e);
    }
  };

  if (typeof as === 'function') {
    return (as as Function)({ children, as, onClick: handleClick, nativeEvent, ...props });
  }

  return createElement(
    HtmlType,
    {
      role: 'button',
      tabIndex: 0,
      onClick: handleClick,
      onKeyDown: (e) => {
        if (e.key === 'Enter') handleClick(e);
      },
      ...props,
    },
    children
  );
};

export const NavigateToWindowWithNativeEvents = ({
  url,
  event = 'link',
  payload,
}: { url: string } & Partial<NativeEventPayload>) => {
  if (isCarvanaNativeApp()) {
    sendNativeMessageWithAbsoluteUrl(event as NativeMessageEvent, {
      url,
      ...payload,
    });
  } else {
    window.location.href = url;
  }
};

export const OpenInNewTabWithNativeEvents = ({
  url,
  event = 'link',
  payload,
}: { url: string } & Partial<NativeEventPayload>) => {
  if (isCarvanaNativeApp()) {
    sendNativeMessageWithAbsoluteUrl(event as NativeMessageEvent, {
      url,
      ...payload,
    });
  } else {
    window.open(url, '_blank');
  }
};
